/**
 * CreateServiceAccountModal Component
 *
 * Modal for creating a new service account.
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useCreateServiceAccount } from '../hooks/useCreateServiceAccount';
import { entityCodeSchema } from '../../../lib/validators/common';

// Validation schema
const createServiceAccountSchema = z.object({
  serviceAccountCode: entityCodeSchema,
  serviceAccountName: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
  description: z.string().max(500, 'Description must be 500 characters or less').optional(),
});

type CreateServiceAccountFormData = z.infer<typeof createServiceAccountSchema>;

interface CreateServiceAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

/**
 * Generate code from name
 * Converts "Service Bot" to "service-bot"
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

export const CreateServiceAccountModal: React.FC<CreateServiceAccountModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CreateServiceAccountFormData>({
    resolver: zodResolver(createServiceAccountSchema),
    defaultValues: {
      serviceAccountCode: '',
      serviceAccountName: '',
      description: '',
    },
  });

  const watchName = watch('serviceAccountName');

  // Auto-generate code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('serviceAccountCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  const { mutate: createServiceAccount, isPending } = useCreateServiceAccount(tenantId, {
    onSuccess: () => {
      setSuccessMessage('Service account created successfully!');
      setErrorMessage('');
      setTimeout(() => {
        reset();
        setAutoGenerateCode(true);
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: CreateServiceAccountFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    createServiceAccount({
      serviceAccountCode: data.serviceAccountCode,
      serviceAccountName: data.serviceAccountName,
      description: data.description || undefined,
    });
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      setAutoGenerateCode(true);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Create Service Account</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Info Alert */}
          <Alert variant="info">
            Service accounts are non-human identities used for automated processes like app jobs.
            They must have APP_DOMAIN roles assigned to define permissions.
          </Alert>

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Name */}
          <FormField
            label="Name"
            required
            error={errors.serviceAccountName?.message}
            helperText="Display name for this service account"
          >
            <Input
              {...register('serviceAccountName')}
              placeholder="Data Sync Bot"
              error={!!errors.serviceAccountName}
            />
          </FormField>

          {/* Service Account Code */}
          <FormField
            label="Code"
            required
            error={errors.serviceAccountCode?.message}
          >
            <Input
              {...register('serviceAccountCode')}
              placeholder="data-sync-bot"
              error={!!errors.serviceAccountCode}
              disabled={autoGenerateCode}
            />
            <div className="mt-2 flex items-center">
              <Checkbox
                id="autoGenerateCode"
                checked={autoGenerateCode}
                onChange={(e) => setAutoGenerateCode(e.target.checked)}
              />
              <label htmlFor="autoGenerateCode" className="ml-2 text-sm text-gray-600">
                Auto-generate code from name
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Code is immutable after creation. Use lowercase letters, numbers, and hyphens.
            </p>
          </FormField>

          {/* Description */}
          <FormField
            label="Description"
            error={errors.description?.message}
            helperText="Purpose or usage description"
          >
            <Textarea
              {...register('description')}
              placeholder="Used for nightly report generation..."
              rows={2}
              error={!!errors.description}
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Create Service Account
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
